import { Form, Row, Col, message, Select, Input, Collapse } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { handleFetchRoles } from "../../api/matrix";
import { handleCreateAutoRequest } from "../../api/autorequests";
import { handleFetchCompanies } from "../../api/companies";
import groupRoles from "../../utils/groupRoles";
import { handleFetchModules } from "../../api/modules";
import TextArea from "antd/es/input/TextArea";
import getBase64 from "../../utils/getBase64";
import spin from "../../assets/images/spinner.svg";
import {
  CheckCircleFilled,
  CloseOutlined,
  DeleteOutlined,
  LeftOutlined,
  Loading3QuartersOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { handleFetchUnits } from "../../api/units";

const REACT_APP_BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

export default function NewAutoRequest() {
  const { user, signout } = useContext(AuthContext);
  const [isSelected, setIsSelected] = useState(false);
  const [selectedFile, setSelectedFile]: any = useState(null);
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState<any>(null);
  const [found, setFound] = useState(false);
  const [isSearching, setSearching] = useState<any>(false);
  const [err, setErr] = useState<any>(null);
  const [form] = Form.useForm();
  const [role, setRole] = useState<any>({});
  const [levels, setLevels] = useState<any>([]);
  const [records, setRecords] = useState<any>(null);
  const [errMessage, setErrMessage] = useState<any>("");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isReport, setReport] = useState<boolean>(false);
  const [businessUnit, setBusinessUnit] = useState<any>(null);
  const navigate = useNavigate();

  const { data: roles } = useQuery({
    queryKey: ["roles"],
    queryFn: handleFetchRoles,
  });

  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: handleFetchCompanies,
  });

  const { data: modules } = useQuery({
    queryKey: ["modules"],
    queryFn: handleFetchModules,
  });

  const { data: units } = useQuery({
    queryKey: ["units"],
    queryFn: handleFetchUnits,
  });

  const unitOptions = units?.records?.map((model: any, index: number) => (
    <Select.Option value={model?._id} key={index}>
      {model?.name}
    </Select.Option>
  ));

  const changeHandler = (event: any) => {
    setSelectedFile(event.target.files[0]);
    setIsSelected(true);
  };

  const handleSelected = (item: any) => {
    setErrMessage("");
    setSelectedUser(item);
    form.setFieldsValue({
      name: item?.name,
      email: item?.email,
      mobile: item?.mobile,
      businessUnitRef: item?.businessUnitRef?._id || null,
    });
    setBusinessUnit(item?.businessUnitRef);
    setRecords([item]);
  };

  const handleSearchUser = async () => {
    try {
      setSearching(true);

      if (!keyword) {
        setSearching(false);
        return;
      }

      const token = localStorage.getItem("token");

      const response = await fetch(
        `${REACT_APP_BASE_API_URL}/v1/users?page=1&limit=10&name=${keyword}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      if (result?.records?.length > 0) {
        setFound(true);
        setRecords(result?.records);
      } else {
        setRecords([]);
        setFound(false);
        setErrMessage("User not found, Please Fill in below");
      }
      setSearching(false);
    } catch (err) {
      console.log(err);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: handleCreateAutoRequest,
    onSuccess: () => {
      message.success("Submitted Successfully");
      clearForm();
      queryClient.invalidateQueries({ queryKey: ["autorequests"] });
      navigate("/autoline/requests");
    },
    onError: (error) => {
      message.error(`${error}`);
    },
  });

  const handleRoleChange = (value: any) => {
    setLevels([]);
    const role = roles?.find((model: any) => model?._id === value);
    setReport(role?.isReport);
    setRole(role);
  };

  const onSubmit = async () => {
    const values = await form.getFieldsValue();

    if (!businessUnit) {
      message.error("Please select business unit");
      return;
    }

    if (!user?._id) {
      message.error("Please login to continue");
      signout();
      return;
    }

    let base64,
      type,
      size = null;

    if (selectedFile) {
      base64 = await getBase64(selectedFile);
      type = selectedFile?.type?.slice(selectedFile?.type - 3);
      size = selectedFile?.size;
    }

    const data = {
      ...values,
      levels,
      name: selectedUser?.name || values?.name,
      email: selectedUser?.email || values?.email,
      mobile: selectedUser?.mobile,
      requestedBy: user?._id,
      requestedFor: selectedUser ? selectedUser?._id : null,
      isNewUser: selectedUser ? false : true,
      image: base64,
      businessUnit: businessUnit?._id,
      businessUnitRef: businessUnit?._id,
    };
    await mutate(data);
  };

  const handleBusinessUnitChange = (value: any) => {
    const unit = units?.records?.find((model: any) => model?._id === value);
    setBusinessUnit(unit);
    form.setFieldsValue({
      businessUnitRef: unit?._id,
    });
  };

  function convertSourceToResult(source: any) {
    return source?.map((item: any) => {
      const valueLabel = ` ${item.name} (Company ${item.code})`;
      return { value: valueLabel, label: valueLabel };
    });
  }
  const roleOptions = roles?.map((model: any, index: number) => (
    <Select.Option value={model?._id} key={index}>
      {model?.name}
    </Select.Option>
  ));

  const moduleOptions = modules?.map((module: any, index: number) => (
    <Select.Option value={module?._id} key={index}>
      {module?.name}
    </Select.Option>
  ));

  const options = convertSourceToResult(companies);

  const handleChange = () => { };

  const handleClearSearch = () => {
    form.resetFields();
    setKeyword("");
    setFound(false);
    setRecords([]);
    setErrMessage("");
    setSearching(false);
    setSelectedUser(null);
  };

  const clearForm = async () => {
    form.resetFields();
    form.setFieldsValue({ file: null });
    setSelectedFile(null);
    setIsSelected(false);
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      handleSearchUser();
    }, 1000);
    return () => clearTimeout(delay);
  }, [keyword]);

  return (
    <div className="flex flex-col border-b pb-2 mb-2 bg-white min-h-screen">
      <div className="flex border-y">
        <Link
          to={"/autoline/requests"}
          className="flex items-center justify-center p-2"
        >
          <LeftOutlined /> Back
        </Link>
      </div>
      <div className="m-2 flex justify-center items-center ">
        <div className="border shadow-lg my-4 bg-white w-4/5 sm:w-2/5">
          <div className="font-bold text-2xl p-4 text-center">
            Autoline Access Rights
          </div>
          <div className="p-6">
            <div className="flex border-b-4 pb-2">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search user or staff name"
                  className={`border py-1 px-2 rounded-sm flex-1 ${err ? "border-red-500" : "border-gray-400"
                    }`}
                  value={keyword}
                  style={{ width: "100%" }}
                  onChange={(e) => {
                    setErr(false);
                    setKeyword(e.target.value);
                  }}
                />
              </div>
              <button
                onClick={() =>
                  found ? handleClearSearch() : handleSearchUser()
                }
                className={`  text-white font-bold py-1 p-2 ml-2 ${found
                    ? "bg-red-500 hover:bg-red-700"
                    : "bg-blue-500 hover:bg-blue-700"
                  }`}
              >
                {isSearching ? (
                  <Loading3QuartersOutlined color="#FFFFFF" />
                ) : (
                  <div className="">
                    {found ? (
                      <CloseOutlined color="#FFFFFF" />
                    ) : (
                      <SearchOutlined color="#FFFFFF" />
                    )}
                  </div>
                )}
              </button>
            </div>

            <div className="mt-1">
              {records?.length > 0 ? (
                <div>
                  {records?.map((item: any, index: number) => (
                    <button
                      className={`flex items-center bg-gray-100 p-2 mb-2 border w-full ${item?._id === selectedUser?._id ? "bg-green-50" : ""
                        }`}
                      onClick={() => handleSelected(item)}
                      key={index}
                    >
                      <div
                        className={`flex justify-center items-center w-6 h-6 -mt-2 mr-2 border border-gray-400 ${item?._id === selectedUser?._id &&
                          "bg-green-500 text-white"
                          }`}
                      >
                        {item?._id === selectedUser?._id && (
                          <div className="">
                            <CheckCircleFilled />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col justify-center flex-1">
                        <div className="flex font-bold">{item?.name}</div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                errMessage && (
                  <div className="text-red-500 text-xxs mb-2">{errMessage}</div>
                )
              )}
            </div>
            <Form
              id="createAutorequest"
              form={form}
              name="normal_login"
              className="login-form"
              onFinish={onSubmit}
              layout="vertical"
            >
              <Row gutter={[16, 0]}>
                <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                  <Form.Item
                    name="name"
                    label="Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter name",
                      },
                    ]}
                  >
                    <Input type="text" placeholder="Name" />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      {
                        required: true,
                        message: "Please enter email",
                      },
                    ]}
                  >
                    <Input type="text" placeholder="Email" />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                  <Form.Item
                    name="mobile"
                    label="Mobile"
                    rules={[
                      {
                        required: false,
                        message: "Please enter mobile",
                      },
                    ]}
                  >
                    <Input type="text" placeholder="Mobile" />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                  <Form.Item
                    name="businessUnitRef"
                    label={"Business Unit"}
                    rules={[
                      {
                        required: true,
                        message: "Please select business unit",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Business Unit"
                      optionFilterProp="children"
                      filterOption={(input: any, option: any) =>
                        option?.children
                          ?.toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onSelect={handleBusinessUnitChange}
                    >
                      {unitOptions}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                  <Form.Item
                    name="role"
                    label={"Role"}
                    rules={[
                      {
                        required: true,
                        message: "Please select role",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Role"
                      optionFilterProp="children"
                      filterOption={(input: any, option: any) =>
                        option?.children
                          ?.toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onSelect={handleRoleChange}
                    >
                      {roleOptions}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                  <Form.Item
                    name="companies"
                    label={"Companies"}
                    rules={[
                      {
                        required: true,
                        message: "Please select companies",
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      style={{ width: "100%" }}
                      placeholder="Please select"
                      onChange={handleChange}
                      options={options}
                    />
                  </Form.Item>
                </Col>
                {isReport && (
                  <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                    <Form.Item
                      name="module"
                      label={"Module"}
                      rules={[
                        {
                          required: true,
                          message: "Please select module",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Module"
                        optionFilterProp="children"
                        filterOption={(input: any, option: any) =>
                          option?.children
                            ?.toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {moduleOptions}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
              </Row>

              {isReport && (
                <Row gutter={[16, 0]}>
                  <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                    <Form.Item
                      name="description"
                      label={"Description"}
                      rules={[
                        {
                          required: true,
                          message: "Please enter description",
                        },
                      ]}
                    >
                      <TextArea placeholder="Description" />
                    </Form.Item>
                  </Col>
                  <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                    <div className="flex flex-col justify-center items-center  h-32 -mt-4">
                      <div className="flex flex-col justify-center items-center">
                        {isSelected ? (
                          <div className="flex flex-col justify-center items-center">
                            <CheckCircleFilled color={"#16a34a"} size={30} />
                            <div className="text-gray-600 mt-2">
                              Image Selected
                            </div>
                            <div className="text-gray-500">
                              {selectedFile?.name}
                            </div>
                            <button
                              className="text-red-200 flex items-center hover:text-red-500"
                              onClick={() => clearForm()}
                            >
                              <DeleteOutlined />{" "}
                              <span className="mx-1">Remove</span>
                            </button>
                          </div>
                        ) : (
                          <Form.Item
                            name="file"
                            label="Upload Image ( Optional )"
                            rules={[
                              {
                                required: false,
                                message: "Please select file",
                              },
                            ]}
                          >
                            <Input
                              type="file"
                              placeholder="Image"
                              onChange={changeHandler}
                            />
                          </Form.Item>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
              <div className="my-2 flex text-green-600">
                {levels?.map((level: any, index: number) => (
                  <div className="mr-2 underline" key={index}>
                    {level?.moduleCode} : Level {level?.level}
                  </div>
                ))}
              </div>
              <div className="font-bold">{role?.name} Modules</div>
              <div className="">
                {groupRoles(role?.levels, "moduleName")?.map(
                  (level: any, index: number) => (
                    <Collapse
                      bordered={false}
                      size="small"
                      key={index}
                      ghost
                      expandIcon={({ isActive }) =>
                        isActive ? (
                          <MinusSquareOutlined />
                        ) : (
                          <PlusSquareOutlined />
                        )
                      }
                      items={[
                        {
                          key: 1,
                          label: (
                            <div className="flex justify-between">
                              <div className="">{level?.code}</div>
                              <div className="">
                                <div className="flex text-gray-600">
                                  Highest Level :
                                  <div className="w-8 text-right">
                                    {level?.level}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ),
                          children: (
                            <div
                              className="-mt-4 border"
                              style={{ fontSize: 12 }}
                            >
                              <div className="grid grid-cols-12">
                                <div className="col-span-12 sm:col-span-3 border-r border-b  px-4">
                                  Module
                                </div>
                                <div className="col-span-12 sm:col-span-3 px-4   border-r border-b">
                                  Permission Level
                                </div>
                                <div className="col-span-12 sm:col-span-6  border-b  px-4">
                                  Module Description
                                </div>
                              </div>
                              {level?.items?.map((item: any, index: number) => (
                                <div className="grid grid-cols-12 " key={index}>
                                  <div className="col-span-12 sm:col-span-3 border-r    px-4">
                                    {item?.moduleCode}
                                  </div>
                                  <div className="col-span-12 sm:col-span-3 border-r    px-4 ">
                                    {item?.level}
                                  </div>
                                  <div className="col-span-12 sm:col-span-6    px-4">
                                    {item?.description}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ),
                        },
                      ]}
                    ></Collapse>
                  )
                )}
              </div>
              <div className="flex justify-center">
                <button className="bg-karimjee hover:bg-karimjee-dark text-white flex items-center justify-center font-bold py-2 px-4 rounded w-32 mb-8 mt-4">
                  {isPending ? (
                    <span>
                      <img src={spin} alt="spin" height="20px" width="20px" />
                    </span>
                  ) : (
                    <span className="text-white">Submit</span>
                  )}
                </button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
